import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Avatar, Badge, Flex, List, Popover } from "antd";

import { convertStringToJson, getTimePassed } from "@/utils";
import NotificationIconWithBadge from "@/assets/svg/NotificationIconWithBadge";
import NotificationSecondaryIcon from "@/assets/svg/NotificationSecondaryIcon";
import NotificationIcon from "@/assets/svg/NotificationIcon";
import useSocketConnection from "@/services/socketService";
import { notificationRequest } from "@/features/user/duck/inbox";
import { notificationTypes } from "@/constants/miscConstants";

import "./Navbar.scss";
import { useTranslation } from "react-i18next";
import LoaderIconAnimated from "@/assets/svg/home/LoaderIconAnimated";

const { Item } = List;

export default function NotificationPopover({ isMobile }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const {
    auth: { userId, token } = {},
    chat: {
      notificationStates: {
        notificationLoading = false,
        data: notificationList = []
      } = {}
    } = {}
  } = useSelector(
    (state) => ({ auth: state.auth, chat: state.chat }),
    shallowEqual
  );

  const { isConnected, isSubscribed, subscribeNotify } = useSocketConnection(
    "userId",
    userId
  );

  useEffect(() => {
    if (token && userId) {
      dispatch(notificationRequest());
    }
  }, [userId]);

  useEffect(() => {
    if (open) {
      dispatch(notificationRequest());
    }
  }, [open]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth); // Update width on window resize
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [window]);

  const hide = () => {
    setOpen(false);
  };

  const notificationHandler = (notifications) => {
    const data = convertStringToJson(notifications?.content);

    if (data?.CHAT) {
      dispatch(notificationRequest());
    }
  };

  useEffect(() => {
    if (isConnected && isSubscribed) {
      subscribeNotify(notificationHandler);
    }
  }, [isConnected]);

  useEffect(() => {
    const handleScroll = () => {
      setOpen(false);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const navigationHandler = (item) => {
    const {
      fromUserId,
      userId,
      referenceId2,
      uuid,
      referenceId,
      alertNotificationType
    } = item;
    hide();
    dispatch(notificationRequest({ notificationId: uuid }));
    alertNotificationType === notificationTypes[0] &&
      navigate(`/property-detail/${referenceId}`);

    alertNotificationType === notificationTypes[1] &&
      navigate(
        `/manage/inbox?propertyId=${referenceId2}&fromUserId=${userId}&toUserId=${fromUserId}`
      );
    alertNotificationType === notificationTypes[2] &&
      navigate(`/manage/profile-settings?tab=subscription`);
  };

  const getTitle = {
    SUBSCRIPTION_PAYMENT_NOTIFICATION: t("notifications.paymentSuccess"),
    PROPERTY_PRICE_UPDATE_NOTIFICATION: t("notifications.propertyUpdate")
  };
  const renderNotificationTitle = (title, time, type) => {
    return (
      <div>
        <div className="flex flex-row justify-between">
          <div className="font-medium text-primary">
            {getTitle[type] || title}
          </div>
          <div className="text-[#A7A7A7] font-normal">
            {getTimePassed(time)}
          </div>
        </div>
      </div>
    );
  };

  const dropdownContent = (
    <List
      loading={{
        spinning: notificationLoading,
        indicator: (
          <div className="loading-overlay !w-28 !top-4 ltr:!left-[36%] rtl:!right-[36%]">
            <LoaderIconAnimated />
          </div>
        )
      }}
      className="notification-list"
      itemLayout="horizontal"
      grid={{ gutter: 1, column: 1 }}
      dataSource={notificationList}
      header={
        <Item>
          <Flex className="notification-header">
            <div>
              <div className="font-medium text-secondary text-2xl">
                {t("notifications.noNotifications")} ({notificationList?.length}
                )
              </div>
            </div>
          </Flex>
        </Item>
      }
      renderItem={(item) => (
        <List.Item
          className={`notification-item ${!item?.isRead ? "message-read" : ""}`}
          onClick={() => navigationHandler(item)}>
          <List.Item.Meta
            avatar={
              <Avatar
                size={40}
                src={item?.profilePictureUrl}
                icon={<NotificationSecondaryIcon />}
              />
            }
            title={renderNotificationTitle(
              item?.fromUserName,
              item?.createdAt,
              item?.alertNotificationType
            )}
            description={item?.message}
          />
        </List.Item>
      )}
    />
  );

  const handleOpenChange = (newOpen) => setOpen(newOpen);
  const hasUnreadMessage = notificationList?.some((item) => !item.isRead);
  const overlayStyle = {
    width: windowWidth > 640 ? "25rem" : "15rem",
    minHeight: "5rem"
  };
  return (
    <Popover
      overlayStyle={overlayStyle}
      trigger="click"
      align="center"
      open={open}
      onOpenChange={handleOpenChange}
      content={dropdownContent}
      placement="bottomRight"
      className="cursor-pointer">
      <div
        onClick={(event) => {
          event.preventDefault();
        }}>
        {!isMobile ? (
          <>
            {hasUnreadMessage ? (
              <NotificationIcon color={isMobile ? "#7B9E7E" : "white"} />
            ) : (
              <NotificationIconWithBadge
                color={isMobile ? "#7B9E7E" : "white"}
              />
            )}
          </>
        ) : (
          <>
            <Badge
              className="ltr:pr-14 rtl:pl-14 relative ltr:left-[31px] rtl:right-[31px] z-10"
              count={notificationList?.length}
              showZero
            />
          </>
        )}
      </div>
    </Popover>
  );
}
